import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { verifyOtp, logoutApi } from '../api/auth';
import { refreshAccessToken } from '../api/refreshAccessToken';
import { decodeJwtPayload, isAdminPayload } from './jwt';
import { AUTH_SESSION_EXPIRED, AUTH_TOKEN_UPDATED } from './sessionEvents';
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from './tokenStorage';
import { AuthContext } from './auth-context';

/** Refresh the access token this far before its `exp` so the session keeps sliding. */
const REFRESH_AHEAD_MS = 2 * 60 * 1000;
/** When the window regains focus, top the token up if it expires within this window. */
const FOCUS_REFRESH_THRESHOLD_MS = 10 * 60 * 1000;
/** Wait before retrying a proactive refresh that failed. */
const RETRY_DELAY_MS = 30 * 1000;

export function AuthProvider({ children }: { children: ReactNode }) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [token, setToken] = useState<string | null>(() => getAccessToken());

    useEffect(() => {
        const onTokenUpdated = (event: Event) => {
            setToken((event as CustomEvent<string>).detail);
        };
        const onSessionExpired = () => {
            setToken(null);
            queryClient.clear();
            navigate('/login', { replace: true });
        };

        window.addEventListener(AUTH_TOKEN_UPDATED, onTokenUpdated);
        window.addEventListener(AUTH_SESSION_EXPIRED, onSessionExpired);
        return () => {
            window.removeEventListener(AUTH_TOKEN_UPDATED, onTokenUpdated);
            window.removeEventListener(AUTH_SESSION_EXPIRED, onSessionExpired);
        };
    }, [navigate, queryClient]);

    // Proactively refresh the access token before it expires so the server-side
    // session keeps sliding while the app is open, and top it up when the window
    // regains focus. The client.ts 401 → refresh path still covers the case where
    // the token expires between refreshes.
    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | undefined;

        const schedule = (minDelay = 5 * 1000) => {
            if (timer) {
                clearTimeout(timer);
            }
            // No refresh token means the user is logged out — stop the loop.
            if (!getRefreshToken()) {
                timer = undefined;
                return;
            }
            const access = getAccessToken();
            const payload = access ? decodeJwtPayload(access) : null;
            const expMs = payload?.exp ? payload.exp * 1000 : 0;
            const delay =
                expMs > 0 ? Math.max(expMs - Date.now() - REFRESH_AHEAD_MS, minDelay) : minDelay;
            timer = setTimeout(() => {
                void refreshAccessToken().then((next) =>
                    schedule(next ? 5 * 1000 : RETRY_DELAY_MS),
                );
            }, delay);
        };

        const onVisible = () => {
            if (!getRefreshToken()) {
                return;
            }
            const access = getAccessToken();
            const payload = access ? decodeJwtPayload(access) : null;
            const expMs = payload?.exp ? payload.exp * 1000 : 0;
            if (expMs > 0 && expMs - Date.now() < FOCUS_REFRESH_THRESHOLD_MS) {
                void refreshAccessToken().then((next) =>
                    schedule(next ? 5 * 1000 : RETRY_DELAY_MS),
                );
            }
        };

        const onFocus = onVisible;
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                onVisible();
            }
        };

        schedule();
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, []);

    const payload = useMemo(() => (token ? decodeJwtPayload(token) : null), [token]);
    const isAdmin = isAdminPayload(payload);

    const loginWithOtp = useCallback(
        async (params: { email?: string; phone?: string; otp: string }) => {
            const tokens = await verifyOtp(params);
            setTokens(tokens);
            setToken(tokens.access_token);
        },
        [],
    );

    const logout = useCallback(async () => {
        await logoutApi();
        clearTokens();
        setToken(null);
    }, []);

    const value = useMemo(
        () => ({
            isAuthenticated: Boolean(token),
            isAdmin,
            payload,
            loginWithOtp,
            logout,
        }),
        [token, isAdmin, payload, loginWithOtp, logout],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
